/**
 * Image to Design — the importer UI, on the flow the family shares.
 *
 * This replaces screens this app had written for itself — a dropzone, header, progress, result
 * and settings sheet that every sibling also had a copy of. They live in `@figma-importers/ui`
 * now, so a fix to one reaches every plugin rather than the one it was noticed in.
 *
 * The store already satisfied the shared contract; `conformsToImporterFlow` in
 * `store/importStore.ts` has been asserting it at compile time all along.
 *
 * Which listing a window belongs to is not known until the sandbox handshakes, so what the
 * dropzone says is read from the store's variant rather than hard-coded. See src/variants.ts.
 */

import { FilePreview, ImporterFlow, LicensePanel } from '@figma-importers/ui';
import { licensing, useImportStore } from './store/importStore';
import { CreatorsNote } from './components/CreatorsNote';

function App() {
  const variant = useImportStore((s) => s.variant);
  const user = useImportStore((s) => s.user);
  const file = useImportStore((s) => s.file);
  const gateBlocked = useImportStore((s) => s.gateBlocked);
  const openLicenseLink = useImportStore((s) => s.openLicenseLink);

  return (
    <ImporterFlow
      store={useImportStore}
      config={{
        app: variant.key,
        accept: variant.accept,
        dropHint: variant.dropHint,
        dropSubhint: variant.subtitle,
        /*
         * "Manage license" is left out rather than shown inert: the licence panel below is
         * where a plan is read and changed, and a second door onto it that opened nothing
         * would be worse than one door.
         */
        help: {
          userId: user?.id ?? null,
          onSendFeedback: () => openLicenseLink('feedback'),
        },
        importLabel: 'Import',
        // The rail pdf has. This importer's input is already an image, so the file is
        // the preview — no rendering, just the picture the user picked.
        preview: <FilePreview file={file} />,
        settings: (
          <>

            <LicensePanel
              licensing={licensing}
              user={user}
              onOpenLink={openLicenseLink}
              blocked={gateBlocked}
            />
            <CreatorsNote />
          </>
        ),
      }}
    />
  );
}

export default App;
